import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, useJsApiLoader } from '@react-google-maps/api';
import ClinicCard from '../components/ClinicCard';
import { getClinics } from '../api/client';
import Footer from '../components/Footer';
import GoogleTranslate from '../components/GoogleTranslate';

export default function HomePage() {
  // Все состояния в начале компонента
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('rating');
  const [clinicsData, setClinicsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [userMarker, setUserMarker] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [infoWindows, setInfoWindows] = useState({});
  const [mapCenter, setMapCenter] = useState({
    lat: 40.4093,
    lng: 49.8671
  });

  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places']
  });

  // Получаем уникальные теги из всех клиник
  const availableTags = useMemo(() => {
    const tags = new Set();
    clinicsData.forEach(clinic => {
      clinic.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).map(tag => ({
      id: tag,
      label: tag.charAt(0).toUpperCase() + tag.slice(1) // Делаем первую букву заглавной
    }));
  }, [clinicsData]);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const response = await getClinics();
        console.log('Полученные данные клиник:', response.clinics.map(c => ({
          id: c.id,
          name: c.name,
          placeId: c.placeId,
          latitude: c.latitude,
          longitude: c.longitude,
          address: c.address
        })));
        setClinicsData(response.clinics || []);
        setError(null);
      } catch (err) {
        console.error('Ошибка загрузки данных', err);
        setError('Не удалось загрузить список клиник. Пожалуйста, попробуйте позже.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const toggleTag = (id) => {
    setSelectedTags((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleSortChange = (e) => setSortBy(e.target.value);

  const handleViewToggle = () => {
    setViewMode(prev => prev === 'list' ? 'map' : 'list');
  };

  const handleClinicClick = (clinicId) => {
    navigate(`/${clinicId}`);
  };

  const filteredAndSortedClinics = useMemo(() => {
    let result = clinicsData;

    if (selectedTags.length > 0) {
      result = result.filter((clinic) =>
        selectedTags.some((tag) => clinic.tags.includes(tag))
      );
    }

    result = result.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'distance':
        case 'price':
          return 0;
        default:
          return 0;
      }
    });

    console.log('Clinics data for map:', result.map(c => ({
      id: c.id,
      name: c.name,
      placeId: c.placeId,
      latitude: c.latitude,
      longitude: c.longitude,
      address: c.address
    })));
    return result;
  }, [selectedTags, sortBy, clinicsData]);

  // Получаем клиники для карты (только с placeId)
  const clinicsForMap = useMemo(() => {
    return filteredAndSortedClinics.filter(clinic => clinic.placeId);
  }, [filteredAndSortedClinics]);

  // Функция для получения местоположения пользователя
  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Геолокация не поддерживается вашим браузером');
      return;
    }

    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newLocation = { lat: latitude, lng: longitude };
        setUserLocation(newLocation);

        // Если карта уже загружена, добавляем или обновляем маркер
        if (mapInstance) {
          if (userMarker) {
            userMarker.setMap(null); // Удаляем старый маркер
          }

          const marker = new window.google.maps.Marker({
            position: newLocation,
            map: mapInstance,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
            title: "Ваше местоположение",
            zIndex: 1000 // Чтобы маркер был поверх других
          });

          setUserMarker(marker);

          // Центрируем карту на пользователе и всех клиниках
          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend(newLocation);
          markers.forEach(m => bounds.extend(m.getPosition()));
          mapInstance.fitBounds(bounds);
        }
      },
      (error) => {
        setLocationError('Не удалось определить ваше местоположение');
        console.error('Geolocation error:', error);
      }
    );
  }, [mapInstance, markers, userMarker]);

  // Очищаем маркер при размонтировании компонента
  useEffect(() => {
    return () => {
      if (userMarker) {
        userMarker.setMap(null);
      }
    };
  }, [userMarker]);

  const mapContainerStyle = {
    width: '100%',
    height: '100%',
    borderRadius: '1rem'
  };

  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      }
    ]
  };

  const onLoad = useCallback((map) => {
    if (clinicsForMap.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      const geocoder = new window.google.maps.Geocoder();
      const newMarkers = [];
      const newInfoWindows = {};
      let markersCount = 0;

      clinicsForMap.forEach(clinic => {
        if (clinic.address) {
          geocoder.geocode({ address: clinic.address }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const position = results[0].geometry.location;
              bounds.extend(position);

              const marker = new window.google.maps.Marker({
                position,
                title: clinic.name,
                map,
                animation: window.google.maps.Animation.DROP
              });

              const infoWindow = new window.google.maps.InfoWindow({
                content: `<div class="p-2"><strong>${clinic.name}</strong><br>${clinic.address}</div>`
              });

              marker.addListener('click', () => {
                Object.values(newInfoWindows).forEach(iw => iw.close());
                infoWindow.open(map, marker);
              });

              newMarkers.push(marker);
              newInfoWindows[clinic.id] = infoWindow;
              markersCount++;

              if (markersCount === clinicsForMap.length) {
                map.fitBounds(bounds);
                const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
                  if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
                    const extendPoint1 = new window.google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01);
                    const extendPoint2 = new window.google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01);
                    bounds.extend(extendPoint1);
                    bounds.extend(extendPoint2);
                  }
                  map.fitBounds(bounds);
                });
              }
            }
          });
        }
      });

      setMarkers(newMarkers);
      setInfoWindows(newInfoWindows);
    }
  }, [clinicsForMap]);

  // Обновляем центрирование при изменении списка клиник
  useEffect(() => {
    if (mapInstance && markers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(marker => bounds.extend(marker.getPosition()));
      mapInstance.fitBounds(bounds);
    }
  }, [filteredAndSortedClinics, mapInstance, markers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold mb-2">Ошибка</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800 relative pb-16">
      <main className="flex-grow">
        <div className="w-full bg-white border-b border-gray-100">
          <div className="max-w-screen-lg mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              {/* Логотип */}
              <div className="flex items-center gap-3">
                <img
                  src="/logo.png"
                  alt="EasyPet Logo"
                  className="w-10 h-10 object-contain"
                />
                <span className="text-xl font-bold text-primary">EASYPET</span>
              </div>

              {/* Переводчик */}
              <div className="flex-shrink-0">
                <GoogleTranslate />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-screen-lg mx-auto p-4">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 transform transition-all duration-300 hover:shadow-xl">
            <h2 className="text-xl font-semibold mb-4">Выберите направления</h2>
            <div className="flex flex-wrap gap-3">
              {availableTags.map((tag) => (
                <button
                  key={tag.id}
                  className={`px-4 py-2 rounded-full text-sm transition-all duration-300 border border-transparent transform hover:scale-105 ${
                    selectedTags.includes(tag.id)
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-gray-100 text-gray-800 hover:bg-primary/10 hover:shadow-sm'
                  }`}
                  onClick={() => toggleTag(tag.id)}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
            <select
              className="px-4 py-2 border border-gray-300 rounded-md text-sm transition-all duration-300 hover:border-green-500 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none"
              value={sortBy}
              onChange={handleSortChange}
            >
              <option value="rating">По рейтингу</option>
              <option value="distance">По расстоянию</option>
              <option value="price">По цене</option>
            </select>

            <button
              className={`px-5 py-2 rounded-md text-sm transition-all duration-300 hover:shadow-md transform hover:scale-105 flex items-center gap-2 ${
                viewMode === 'map' 
                  ? 'bg-gray-600 text-white hover:bg-gray-700' 
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
              onClick={handleViewToggle}
            >
              
              {viewMode === 'map' ? 'Показать список' : 'Показать на карте'}
            </button>
          </div>

          {viewMode === 'list' ? (
            <div className="grid gap-6">
              {filteredAndSortedClinics.map((clinic) => (
                <ClinicCard 
                  key={clinic.id}
                  clinic={clinic} 
                  directions={availableTags}
                  onClick={() => handleClinicClick(clinic.id)}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-6 h-[600px] relative">
              <div className="absolute top-4 right-4 z-10 flex gap-2">
                <button
                  onClick={getUserLocation}
                  className={`px-4 py-2 rounded-md text-sm transition-colors flex items-center gap-2 shadow-md ${
                    userLocation 
                      ? 'bg-primary text-white hover:bg-primary/90' 
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                  title={locationError || (userLocation ? "Обновить местоположение" : "Показать мое местоположение")}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {locationError ? 'Ошибка' : (userLocation ? 'Обновить' : 'Моё местоположение')}
                </button>
              </div>
              <div className="absolute inset-0 rounded-2xl overflow-hidden">
                {clinicsForMap.length > 0 && isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={12}
                    options={mapOptions}
                    onLoad={onLoad}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <p className="text-gray-500">Нет доступных клиник для отображения на карте</p>
                  </div>
                )}
              </div>
              <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">
                    Найдено клиник: {filteredAndSortedClinics.length}
                    {clinicsForMap.length !== filteredAndSortedClinics.length && (
                      <span className="ml-2 text-blue-600">
                        (на карте: {clinicsForMap.length})
                      </span>
                    )}
                  </p>
                  {userLocation && (
                    <p className="text-sm text-blue-600">
                      Ваше местоположение отмечено на карте
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}